import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { StripePaymentSetupResult } from '@app/core/models';
import { LoadingContainerComponent } from './loading-container.component';
import { LoadingIndicatorComponent } from './loading-indicator.component';
import { LoadingButtonComponent } from './loading-button.component';

type StripeFactory = (publishableKey: string) => StripeInstance;

interface StripeInstance {
  elements(options: { clientSecret: string; appearance?: Record<string, unknown> }): StripeElements;
  confirmSetup(options: {
    elements: StripeElements;
    confirmParams?: {
      payment_method_data?: {
        billing_details?: {
          name?: string;
          email?: string;
        };
      };
    };
    redirect: 'if_required';
  }): Promise<{
    error?: { message?: string };
    setupIntent?: { id: string; payment_method?: string | { id: string } };
  }>;
}

interface StripeElements {
  create(type: 'payment', options?: Record<string, unknown>): StripePaymentElement;
  create(type: 'expressCheckout', options?: Record<string, unknown>): StripeExpressCheckoutElement;
  submit(): Promise<{ error?: { message?: string } }>;
}

interface StripePaymentElement {
  on(event: 'ready', handler: () => void): void;
  on(event: 'loaderror', handler: (event: { error?: { message?: string } }) => void): void;
  mount(target: HTMLElement | string): void;
  unmount(): void;
  destroy(): void;
}

interface StripeExpressCheckoutElement {
  on(event: 'availablepaymentmethodschange', handler: (event: {
    paymentMethods?: { applePay?: boolean; googlePay?: boolean } | null
  }) => void): void;
  on(event: 'confirm', handler: () => void): void;
  mount(target: HTMLElement | string): void;
  unmount(): void;
  destroy(): void;
}

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

let stripeScriptPromise: Promise<void> | null = null;

function ensureStripeScript(): Promise<void> {
  if (window.Stripe) {
    return Promise.resolve();
  }

  if (stripeScriptPromise) {
    return stripeScriptPromise;
  }

  stripeScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://js.stripe.com/v3/"]'
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Unable to load Stripe.js.')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Stripe.js.'));
    document.head.appendChild(script);
  });

  return stripeScriptPromise;
}

@Component({
  selector: 'app-stripe-card-setup',
  standalone: true,
  imports: [CommonModule, LoadingContainerComponent, LoadingIndicatorComponent, LoadingButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-slate-950">{{ title }}</p>
          <p class="mt-1 text-sm text-slate-500">
            Card details are collected securely by Stripe and never stored directly in Action Ledgers.
          </p>
          @if (allowWallets) {
            <p class="mt-1 text-xs text-slate-500">Apple Pay and Google Pay appear when available on this device and enabled for this site.</p>
          }
        </div>

        <div class="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            [disabled]="submitting() || busy || disabled"
            (click)="cancel()"
          >
            Cancel
          </button>

          <button appLoadingButton [loading]="submitting() || busy"
            type="button"
            class="rounded-xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
            [disabled]="disabled || mounting() || !ready()"
            (click)="confirmSetup()"
          >
            {{ busy ? busyLabel : submitting() ? submittingLabel : submitLabel }}
          </button>
        </div>
      </div>

      <div class="mt-5 space-y-4">
        @if (allowWallets) {
          <div [class.hidden]="walletsChecked() && !walletsAvailable()"
            [style.visibility]="walletsAvailable() && ready() ? 'visible' : 'hidden'">
            <p class="mb-2 text-sm font-semibold text-slate-900">Quick wallet setup</p>
            <div #expressHost></div>
            <p class="mt-3 text-center text-xs text-slate-500">Or enter a card below</p>
          </div>
          @if (walletsChecked() && !walletsAvailable()) {
            <p class="text-xs text-slate-500">Wallet options are unavailable for this checkout. You can use a card below.</p>
          }
        }
        <app-loading-container [loading]="mounting()" message="Loading secure payment form…">
        <div
          #paymentHost
          class="min-h-20 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-4"
        ></div>
        </app-loading-container>

        @if (submitting()) {
          <app-loading-indicator [message]="submittingLabel" />
          <p class="text-sm text-slate-600">Complete any verification requested by your bank. Please do not submit again.</p>
        }

        @if (mountError()) {
          <div class="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {{ mountError() }}
          </div>
        }
      </div>
    </section>
  `
})
export class StripeCardSetupComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) clientSecret = '';
  @Input({ required: true }) publishableKey = '';
  @Input() billingName = '';
  @Input() billingEmail = '';
  @Input() title = 'Add payment method';
  @Input() submitLabel = 'Save card';
  @Input() submittingLabel = 'Saving...';
  @Input({ transform: booleanAttribute }) busy = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() busyLabel = 'Saving payment information…';
  @Input({ transform: booleanAttribute }) allowWallets = false;

  @Output() submitted = new EventEmitter<StripePaymentSetupResult>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() confirmingChange = new EventEmitter<boolean>();

  @ViewChild('paymentHost', { static: true }) private paymentHost?: ElementRef<HTMLDivElement>;
  @ViewChild('expressHost') private expressHost?: ElementRef<HTMLDivElement>;

  readonly mounting = signal(false);
  readonly submitting = signal(false);
  readonly ready = signal(false);
  readonly mountError = signal('');
  readonly walletsAvailable = signal(false);
  readonly walletsChecked = signal(false);

  private stripe: StripeInstance | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;
  private expressCheckoutElement: StripeExpressCheckoutElement | null = null;
  private viewReady = false;
  private mountGeneration = 0;
  private mountTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  async ngAfterViewInit(): Promise<void> {
    this.viewReady = true;
    await this.mountPaymentElement();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.viewReady) {
      return;
    }

    if (changes['clientSecret'] || changes['publishableKey']) {
      await this.mountPaymentElement();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mountGeneration++;
    clearTimeout(this.mountTimer);
    this.destroyPaymentElement();
  }

  cancel(): void {
    if (!this.submitting() && !this.busy && !this.disabled) this.cancelled.emit();
  }

  async confirmSetup(fromWallet = false): Promise<void> {
    if (this.submitting() || this.busy || this.disabled) return;
    if (!this.stripe || !this.elements || this.mounting() || !this.ready()) {
      this.mountError.set('Stripe payment form is not ready yet.');
      return;
    }

    this.submitting.set(true);
    this.confirmingChange.emit(true);
    this.mountError.set('');

    try {
      if (fromWallet) {
        const submission = await this.elements.submit();
        if (submission.error) {
          this.mountError.set(submission.error.message || 'Unable to confirm the wallet.');
          return;
        }
      }
      const result = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: this.billingName || undefined,
              email: this.billingEmail || undefined
            }
          }
        },
        redirect: 'if_required'
      });

      if (this.destroyed) return;

      if (result.error?.message) {
        this.mountError.set(result.error.message);
        return;
      }

      const setupIntentId = result.setupIntent?.id;
      const paymentMethodValue = result.setupIntent?.payment_method;
      const paymentMethodId =
        typeof paymentMethodValue === 'string' ? paymentMethodValue : paymentMethodValue?.id;

      if (!setupIntentId || !paymentMethodId) {
        this.mountError.set('Stripe did not return a usable payment method.');
        return;
      }

      this.submitted.emit({ setupIntentId, paymentMethodId });
    } catch (error) {
      this.mountError.set(
        error instanceof Error ? error.message : 'Unable to confirm card details right now.'
      );
    } finally {
      this.submitting.set(false);
      if (!this.destroyed) this.confirmingChange.emit(false);
    }
  }

  private async mountPaymentElement(): Promise<void> {
    const generation = ++this.mountGeneration;
    clearTimeout(this.mountTimer);
    if (!this.paymentHost?.nativeElement) {
      return;
    }

    if (!this.clientSecret || !this.publishableKey) {
      this.mounting.set(false);
      this.ready.set(false);
      this.mountError.set('Stripe is not configured yet for this environment.');
      this.destroyPaymentElement();
      return;
    }

    this.mounting.set(true);
    this.ready.set(false);
    this.mountError.set('');
    this.mountTimer = setTimeout(() => {
      if (generation !== this.mountGeneration || this.destroyed) return;
      this.mountGeneration++;
      this.mounting.set(false);
      this.ready.set(false);
      this.mountError.set('The secure payment form took too long to load. Cancel and reopen payment information to try again.');
      this.destroyPaymentElement();
    }, 30_000);

    try {
      await ensureStripeScript();
      if (generation !== this.mountGeneration || this.destroyed) return;

      if (!window.Stripe) {
        throw new Error('Stripe.js is not available.');
      }

      this.destroyPaymentElement();

      this.stripe = window.Stripe(this.publishableKey);
      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0f172a',
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            borderRadius: '14px'
          }
        }
      });
      if (this.allowWallets && this.expressHost?.nativeElement) {
        this.expressCheckoutElement = this.elements.create('expressCheckout', {
          paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never' },
          buttonType: { applePay: 'subscribe', googlePay: 'subscribe' }
        });
        this.expressCheckoutElement.on('availablepaymentmethodschange', ({ paymentMethods }) => {
          if (generation !== this.mountGeneration || this.destroyed) return;
          this.walletsAvailable.set(!!(paymentMethods?.applePay || paymentMethods?.googlePay));
          this.walletsChecked.set(true);
        });
        this.expressCheckoutElement.on('confirm', () => { void this.confirmSetup(true); });
        this.expressCheckoutElement.mount(this.expressHost.nativeElement);
      }
      this.paymentElement = this.elements.create('payment', {
        layout: 'tabs',
        paymentMethodOrder: ['card'],
        fields: {
          billingDetails: {
            address: 'if_required'
          }
        },
        wallets: {
          applePay: this.allowWallets ? 'auto' : 'never',
          googlePay: this.allowWallets ? 'auto' : 'never',
          link: 'never'
        },
        terms: {
          card: 'never'
        },
        defaultValues: {
          billingDetails: {
            name: this.billingName || undefined,
            email: this.billingEmail || undefined
          }
        }
      });
      this.paymentElement.on('ready', () => {
        if (generation !== this.mountGeneration || this.destroyed) return;
        clearTimeout(this.mountTimer);
        this.mounting.set(false);
        this.ready.set(true);
      });
      this.paymentElement.on('loaderror', (event) => {
        if (generation !== this.mountGeneration || this.destroyed) return;
        clearTimeout(this.mountTimer);
        this.mounting.set(false);
        this.ready.set(false);
        this.mountError.set(event.error?.message || 'Unable to load secure card entry. Cancel and try again.');
      });
      this.paymentElement.mount(this.paymentHost.nativeElement);
    } catch (error) {
      if (generation !== this.mountGeneration || this.destroyed) return;
      clearTimeout(this.mountTimer);
      this.mounting.set(false);
      this.mountError.set(
        error instanceof Error ? error.message : 'Unable to initialize secure card entry.'
      );
    }
  }

  private destroyPaymentElement(): void {
    this.expressCheckoutElement?.unmount();
    this.expressCheckoutElement?.destroy();
    this.expressCheckoutElement = null;
    this.walletsAvailable.set(false);
    this.walletsChecked.set(false);
    this.paymentElement?.unmount();
    this.paymentElement?.destroy();
    this.paymentElement = null;
    this.elements = null;
    this.stripe = null;
  }
}
